import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Plus, Loader2, Trash2 } from "lucide-react";
import { listSubscriptions, createSubscription, cancelSubscription } from "../../lib/api/admin-subscriptions";
import { listPlans } from "../../lib/api/admin-plans";
import { api } from "../../lib/api/client";
import type { SubscriptionCreateRequest } from "../../types/usage";

interface UserOption {
  id: string;
  email: string;
  display_name?: string;
}

export function SubscriptionsPage() {
  const queryClient = useQueryClient();
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState<SubscriptionCreateRequest>({ user_id: "", plan_id: "" });

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: listSubscriptions,
  });

  const { data: plans } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: listPlans,
  });

  const { data: users } = useQuery({
    queryKey: ["admin-users-list"],
    queryFn: async (): Promise<UserOption[]> => {
      const response = await api.get("/users", { params: { page: 1, size: 100 } });
      return response.data.users;
    },
  });

  const assignMutation = useMutation({
    mutationFn: (data: SubscriptionCreateRequest) => createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
      setShowAssignForm(false);
      setAssignForm({ user_id: "", plan_id: "" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-gray-600" />
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
        </div>
        <button
          onClick={() => setShowAssignForm(!showAssignForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Assign Plan
        </button>
      </div>

      {showAssignForm && (
        <div className="bg-white shadow rounded-lg p-4 flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
            <select
              value={assignForm.user_id}
              onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a user</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}{user.display_name ? ` (${user.display_name})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={assignForm.plan_id}
              onChange={(e) => setAssignForm({ ...assignForm, plan_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a plan</option>
              {plans?.filter(p => p.is_active).map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.display_name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => assignMutation.mutate(assignForm)}
            disabled={!assignForm.user_id || !assignForm.plan_id || assignMutation.isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Assign
          </button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subscriptions?.map((sub) => (
              <tr key={sub.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {sub.user_email || sub.user_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {sub.plan?.display_name || sub.plan_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded text-xs ${
                    sub.status === "active" ? "bg-green-100 text-green-800" :
                    sub.status === "cancelled" ? "bg-gray-100 text-gray-800" :
                    "bg-red-100 text-red-800"
                  }`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {sub.current_usage !== null && sub.usage_limit !== null
                    ? `${sub.current_usage} / ${sub.usage_limit}`
                    : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(sub.activated_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sub.status === "active" && (
                    <button
                      onClick={() => cancelMutation.mutate(sub.id)}
                      disabled={cancelMutation.isPending}
                      className="text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
